import { Button, Form, message, Tabs } from 'antd';
import { useForm } from 'antd/es/form/Form';
import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';
import { UniversityFormComponent } from './UniversityFormComponent';
import { DepartmentFormComponent } from './DepartmentFormComponent';
import { StudyLineFormComponent } from './StudyLineFormComponent';
import { InternshipSessionFormComponent } from './InternshipSessionFormComponent';
import type {
  DepartmentPostDto,
  UniversityPostDto,
  StudyLinePostDto,
  SessionPostDto,
  StudyLevel,
  University,
} from '../../Types/interfaces';
import { StudyLineType } from '../../Types/interfaces';
import { PostUniversity } from '../../helper/apiHelper';
import { useTranslation } from 'react-i18next';

export const UniversityFormMultipleComponents = (): ReactNode => {
  const [messageApi] = message.useMessage();
  const [t, i18n] = useTranslation();
  const [form] = useForm();
  const [activeTab, setActiveTab] = useState<string>('universityTab');
  const departmentNames = useRef<Array<string>>([]);
  const studyLineNames = useRef<Array<Array<string>>>([]);

  useEffect(() => {
    const onLanguageChanged = (): void => {};

    i18n.on('languageChanged', onLanguageChanged);

    return (): void => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  const changeActiveTab = (key: string): void => {
    setActiveTab(key);
  };

  const handleSubmit = async (uni: University): Promise<void> => {
    try {
      if (uni.departments === undefined) {
        throw new Error(t('uniForm.errors.missingDepartments'));
      }

      if (uni.studyLines === undefined) {
        throw new Error(t('uniForm.errors.missingStudyLines'));
      }

      if (uni.sessions === undefined) {
        throw new Error(t('uniForm.errors.missingSessions'));
      }

      const departments: Array<DepartmentPostDto> = uni.departments.map(
        (
          dep: {
            departmentName: string;
            deans: Array<string>;
            representatives: Array<string>;
          },
          departmentIndex: number
        ) => {
          const studyLines: Array<StudyLinePostDto> = uni.studyLines[
            departmentIndex
          ].map(
            (
              sl: {
                studyLineName: string;
                studyLineLevel: StudyLevel;
                coordinators: Array<string>;
              },
              studyLineIndex: number
            ) => {
              const sessions: Array<SessionPostDto> = uni.sessions[
                studyLineIndex
              ].map(
                (s: {
                  description: string;
                  yearOfStudy: number;
                  courseCredit: number;
                  sessionHours: number;
                }) => {
                  return {
                    description: s.description,
                    yearOfStudy: s.yearOfStudy,
                    courseCredit: s.courseCredit.toString(),
                    hours: s.sessionHours,
                  };
                }
              );
              return {
                name: sl.studyLineName,
                studyLineLevel: sl.studyLineLevel,
                sessions: sessions,
                studyLineType: StudyLineType.ComputerScience,
                universityCoordinatorsEmails: sl.coordinators,
              };
            }
          );
          return {
            name: dep.departmentName,
            studyLines: studyLines,
            deansEmails: dep.deans || [],
            representativesEmails: dep.representatives || [],
          };
        }
      );

      const university: UniversityPostDto = {
        name: uni.university.name,
        rectorsEmails: uni.university.rectors || [],
        workPlaceSafetySupervisorsEmails:
          uni.university.workplaceSafetySupervisors || [],
        legalDepartmentMembersEmails:
          uni.university.legalDepartmentMembers || [],

        departmentsSetup: departments,
      };

      await PostUniversity(university)
        .then(() => {
          messageApi.success({
            content: t('uniForm.regSuccess'),
          });
          form.resetFields();
        })
        .catch(function () {
          messageApi.error({
            content: t('uniForm.regError'),
          });
        });

      form.resetFields();
      departmentNames.current = [];
      studyLineNames.current = [];
      message.success(t('uniForm.regSuccess'));
    } catch (error: unknown) {
      console.log(error);
      if (error instanceof Error) {
        message.error(error.message);
      }
    }
  };

  return (
    <Form form={form} onFinish={handleSubmit}>
      <Tabs activeKey={activeTab} onChange={changeActiveTab}>
        <Tabs.TabPane
          key="universityTab"
          tab={t('uniForm.universityLabel')}
          forceRender
        >
          <UniversityFormComponent />
        </Tabs.TabPane>

        <Tabs.TabPane
          key="departmentsTab"
          tab={t('uniForm.departmentsLabel')}
          forceRender
        >
          <DepartmentFormComponent departmentNames={departmentNames} />
        </Tabs.TabPane>

        <Tabs.TabPane
          key="studyLinesTab"
          tab={t('uniForm.studyLinesLabel')}
          forceRender
        >
          <StudyLineFormComponent
            departmentNames={departmentNames}
            studyLineNames={studyLineNames}
          />
        </Tabs.TabPane>

        <Tabs.TabPane
          key="sessionsTab"
          tab={t('uniForm.sessionsLabel')}
          forceRender
        >
          <InternshipSessionFormComponent studyLineNames={studyLineNames} />
        </Tabs.TabPane>
      </Tabs>

      <Button
        style={{ marginTop: '1%', width: '100%' }}
        type="primary"
        htmlType="submit"
      >
        {t('uniForm.register')}
      </Button>
    </Form>
  );
};
